import {
  ProjectSizeCategory,
  CategoryRule,
  CalculationInput,
  CalculationResult,
} from './calculator-types';

// 1. Database Configuration (Extracted 1:1 from source)
const PROJECT_CATEGORIES: Record<ProjectSizeCategory, CategoryRule> = {
  XS: {
    name: 'Simple Departmental Tools',
    slocRange: [2000, 8000],
    sizeRange: [1, 5],
    users: '<100',
    screens: '1-3',
    integrations: '0-1',
    complexity: 1,
    minEngMonthsTrad: 2,
    maxEngMonthsTrad: 4,
    minEngMonthsVelx: 1,
    maxEngMonthsVelx: 2,
    minTokens: 93,
    maxTokens: 186,
    minMonthsTrad: 2,
    maxMonthsTrad: 3,
  },
  S: {
    name: 'Department Business Apps',
    slocRange: [8001, 25000],
    sizeRange: [5, 15],
    users: '100-1000',
    screens: '5-10',
    integrations: '1-2',
    complexity: 1.5,
    minEngMonthsTrad: 7,
    maxEngMonthsTrad: 11,
    minEngMonthsVelx: 3,
    maxEngMonthsVelx: 5,
    minTokens: 375,
    maxTokens: 569,
    minMonthsTrad: 3,
    maxMonthsTrad: 5,
  },
  M: {
    name: 'Cross-Department Systems',
    slocRange: [25001, 100000],
    sizeRange: [15, 75],
    users: '1000-10000',
    screens: '15-25',
    integrations: '3-6',
    complexity: 2,
    minEngMonthsTrad: 32,
    maxEngMonthsTrad: 49,
    minEngMonthsVelx: 15,
    maxEngMonthsVelx: 21,
    minTokens: 1676,
    maxTokens: 2513,
    minMonthsTrad: 6,
    maxMonthsTrad: 10,
  },
  L: {
    name: 'Mission-Critical Enterprise',
    slocRange: [100001, 500000],
    sizeRange: [75, 400],
    users: '10000+',
    screens: '50+',
    integrations: '10+',
    complexity: 2.5,
    minEngMonthsTrad: 109,
    maxEngMonthsTrad: 163,
    minEngMonthsVelx: 52,
    maxEngMonthsVelx: 71,
    minTokens: 5611,
    maxTokens: 8417,
    minMonthsTrad: 11,
    maxMonthsTrad: 18,
  },
  XL: {
    name: 'Digital Transformation',
    slocRange: [500001, 2000000],
    sizeRange: [400, 1500],
    users: '25000+',
    screens: 'Multiple',
    integrations: '15+',
    complexity: 3,
    minEngMonthsTrad: 248,
    maxEngMonthsTrad: 372,
    minEngMonthsVelx: 118,
    maxEngMonthsVelx: 163,
    minTokens: 12826,
    maxTokens: 19239,
    minMonthsTrad: 15,
    maxMonthsTrad: 24,
  },
};

// 2. Helper Functions
function interpolateValue(min: number, max: number, scale: number): number {
  return min + (max - min) * scale;
}

function calculateTokensFromEngMonths(
  velxEngMonths: number,
  minEngMonths: number,
  maxEngMonths: number,
  minTokens: number,
  maxTokens: number,
): number {
  if (velxEngMonths <= minEngMonths) {
    return minTokens;
  }
  if (velxEngMonths >= maxEngMonths) {
    return maxTokens;
  }
  const scale = (velxEngMonths - minEngMonths) / (maxEngMonths - minEngMonths);
  return interpolateValue(minTokens, maxTokens, scale);
}

function determineCategory(
  sloc: number,
  size: number,
): { category: ProjectSizeCategory; isExtrapolated: boolean } {
  for (const [key, rules] of Object.entries(PROJECT_CATEGORIES)) {
    if (
      sloc >= rules.slocRange[0] &&
      sloc <= rules.slocRange[1] &&
      size >= rules.sizeRange[0] &&
      size <= rules.sizeRange[1]
    ) {
      return { category: key as ProjectSizeCategory, isExtrapolated: false };
    }
  }

  // Fallback / Extrapolation logic from original script
  let category: ProjectSizeCategory;
  if (sloc <= 8000) {
    category = 'XS';
  } else if (sloc <= 25000) {
    category = 'S';
  } else if (sloc <= 100000) {
    category = 'M';
  } else if (sloc <= 500000) {
    category = 'L';
  } else {
    category = 'XL';
  }

  return { category, isExtrapolated: true };
}

// 3. Main Calculation Function
export function calculateVelxImpact(
  input: CalculationInput,
): CalculationResult {
  // Defaults
  // Traditional dev cost: Fully-loaded US developer (~$190,600/year = ~$15,883/month)
  // Breakdown based on BLS 2024 data + industry standards:
  // - Base Salary: $131,450/year (BLS 2024 median for Software Developers)
  // - Benefits: +30% (~$39,435)
  // - Taxes & Insurance: +8% (~$10,516) - Social Security, Medicare, unemployment
  // - Equipment & Workspace: ~$3,500
  // - Recruitment Costs: ~$4,700 (amortized)
  // - Training & Development: ~$1,000
  // Total: ~$190,600/year = $15,883/month
  const tradDevCostPerMonth = 15883;
  // Velx-Devsu dev cost: $8,000/month (outsourced with AI-augmented development)
  const velxDevCostPerMonth = input.devCostPerMonth ?? 8000;
  const tokenCost = input.tokenCostPer1k ?? 0.03;

  // Calculate complexity multiplier from checkboxes
  // These factors increase traditional development effort/cost
  let complexityMultiplier = 1.0;
  if (input.hasLegacyTech) {
    complexityMultiplier += 0.2;
  } // +20%
  if (input.hasPoorDocumentation) {
    complexityMultiplier += 0.2;
  } // +20%
  if (input.hasDistributedTeams) {
    complexityMultiplier += 0.1;
  } // +10%

  // Validation - use minimum values if below threshold
  const sloc = Math.max(input.sloc, 2000);
  const repoSizeMB = Math.max(input.repoSizeMB, 1);

  // Determine Category
  const { category, isExtrapolated } = determineCategory(sloc, repoSizeMB);
  const catData = PROJECT_CATEGORIES[category];

  // Calculate Project Scale (0.0 to 1.0 within category range)
  const slocMin = catData.slocRange[0];
  const slocMax = catData.slocRange[1];
  // Clamp scale between 0 and 1
  const projectScale = Math.min(
    1,
    Math.max(0, (sloc - slocMin) / (slocMax - slocMin)),
  );

  // Interpolate Engineering Months (Total Effort)
  let tradEngMonths = interpolateValue(
    catData.minEngMonthsTrad,
    catData.maxEngMonthsTrad,
    projectScale,
  );
  let velxEngMonths = interpolateValue(
    catData.minEngMonthsVelx,
    catData.maxEngMonthsVelx,
    projectScale,
  );

  // Apply complexity multiplier from checkboxes (affects only traditional development)
  tradEngMonths = tradEngMonths * complexityMultiplier;

  // Cap at maximums defined in rules (multiplied for traditional if factors apply)
  tradEngMonths = Math.min(
    tradEngMonths,
    catData.maxEngMonthsTrad * complexityMultiplier,
  );
  velxEngMonths = Math.min(velxEngMonths, catData.maxEngMonthsVelx);

  // --- Traditional Calculations ---

  // Avg duration is the midpoint of the category's typical duration range
  const avgTradDuration = (catData.minMonthsTrad + catData.maxMonthsTrad) / 2;

  // Determine Traditional Team Size
  let numTradDevs = input.numTradDevs;
  if (!numTradDevs) {
    numTradDevs = Math.ceil(tradEngMonths / avgTradDuration);
    // Clamp team size (1 to 12) per original logic
    numTradDevs = Math.max(1, Math.min(numTradDevs, 12));
  }

  // Recalculate duration based on specific team size
  const actualTradDuration = tradEngMonths / numTradDevs;
  const actualTradDevMonths = numTradDevs * actualTradDuration; // Should equal tradEngMonths
  const tradTotalCost = actualTradDevMonths * tradDevCostPerMonth;

  // --- Velx Calculations ---

  // Velx duration is derived from traditional duration adjusted by the reduction factor
  const velxReductionFactor = velxEngMonths / tradEngMonths;
  // Note: The logic here follows the source: target duration scales with effort reduction
  const targetVelxDuration = actualTradDuration * velxReductionFactor;

  // Determine Velx Team Size
  let numVelxDevs = input.numVelxDevs;
  if (!numVelxDevs) {
    numVelxDevs = Math.ceil(velxEngMonths / targetVelxDuration);
    // Clamp team size (1 to 8) per original logic
    numVelxDevs = Math.max(1, Math.min(numVelxDevs, 8));
  }

  const actualVelxDuration = velxEngMonths / numVelxDevs;
  const actualVelxDevMonths = numVelxDevs * actualVelxDuration;
  const velxDevCost = actualVelxDevMonths * velxDevCostPerMonth;

  // --- AI Costs ---
  let aiTokensM = calculateTokensFromEngMonths(
    actualVelxDevMonths,
    catData.minEngMonthsVelx,
    catData.maxEngMonthsVelx,
    catData.minTokens,
    catData.maxTokens,
  );
  aiTokensM = Math.min(aiTokensM, catData.maxTokens); // Cap tokens

  const aiCost = aiTokensM * 1000 * tokenCost;
  const velxTotalCost = velxDevCost + aiCost;

  // --- Savings & Stats ---
  const dollarSavings = tradTotalCost - velxTotalCost;
  const costReduction =
    dollarSavings > 0 ? (dollarSavings / tradTotalCost) * 100 : 0;
  // Time reduction based on calendar duration
  const timeReduction =
    (Math.max(0, actualTradDuration - actualVelxDuration) /
      actualTradDuration) *
    100;
  const efficiencyGain = actualTradDevMonths / actualVelxDevMonths;

  return {
    meta: {
      category,
      isExtrapolated,
      description: catData.name,
      complexityMultiplier: catData.complexity,
      characteristics: {
        users: catData.users,
        screens: catData.screens,
        integrations: catData.integrations,
      },
    },
    traditional: {
      teamSize: numTradDevs,
      durationMonths: Number(actualTradDuration.toFixed(2)),
      totalDevMonths: Number(actualTradDevMonths.toFixed(2)),
      totalCost: Number(tradTotalCost.toFixed(2)),
    },
    velx: {
      teamSize: numVelxDevs,
      durationMonths: Number(actualVelxDuration.toFixed(2)),
      totalDevMonths: Number(actualVelxDevMonths.toFixed(2)),
      devCost: Number(velxDevCost.toFixed(2)),
      aiTokensMillions: Number(aiTokensM.toFixed(2)),
      aiCost: Number(aiCost.toFixed(2)),
      totalCost: Number(velxTotalCost.toFixed(2)),
    },
    savings: {
      amount: Number(dollarSavings.toFixed(2)),
      costReductionPercent: Number(costReduction.toFixed(0)),
      timeReductionPercent: Number(timeReduction.toFixed(0)),
      efficiencyGainMultiplier: Number(efficiencyGain.toFixed(1)),
    },
  };
}
